import { useState } from 'react';
import { CustomPage } from '@/lib/themeConfig';
import PageEditor from './PageEditor';

interface Props {
  pages: CustomPage[];
  onChange: (pages: CustomPage[]) => void;
}

function generateId() {
  return Math.random().toString(36).slice(2, 10);
}

function slugify(val: string) {
  return val
    .toLowerCase()
    .replace(/ğ/g, 'g').replace(/ü/g, 'u').replace(/ş/g, 's')
    .replace(/ı/g, 'i').replace(/ö/g, 'o').replace(/ç/g, 'c')
    .replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
}

export default function PagesSection({ pages, onChange }: Props) {
  const [view, setView] = useState<'list' | 'new' | 'edit'>('list');
  const [editingPage, setEditingPage] = useState<CustomPage | null>(null);
  const [newPageName, setNewPageName] = useState('');
  const [newPageSlug, setNewPageSlug] = useState('');
  const [slugError, setSlugError] = useState('');

  const handleCreatePage = () => {
    if (!newPageName.trim() || !newPageSlug.trim()) return;
    const slug = newPageSlug.trim().replace(/^\//, '');
    const exists = pages.some(p => p.seo.slug === slug);
    if (exists) { setSlugError('Bu URL zaten kullanılıyor.'); return; }
    const now = new Date().toISOString();
    const newPage: CustomPage = {
      id: generateId(),
      name: newPageName.trim(),
      seo: { title: newPageName.trim(), description: '', keywords: '', slug },
      sections: [],
      created_at: now,
      updated_at: now,
    };
    onChange([...pages, newPage]);
    setEditingPage(newPage);
    setView('edit');
    setNewPageName('');
    setNewPageSlug('');
    setSlugError('');
  };

  const handleDeletePage = (id: string) => {
    onChange(pages.filter(p => p.id !== id));
  };

  const handleSavePage = (updated: CustomPage) => {
    onChange(pages.map(p => p.id === updated.id ? { ...updated, updated_at: new Date().toISOString() } : p));
    setEditingPage(updated);
  };

  if (view === 'edit' && editingPage) {
    return (
      <PageEditor
        page={editingPage}
        onSave={handleSavePage}
        onBack={() => { setView('list'); setEditingPage(null); }}
      />
    );
  }

  if (view === 'new') {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => { setView('list'); setNewPageName(''); setNewPageSlug(''); setSlugError(''); }}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-stone-600" />
          </button>
          <div>
            <h2 className="text-lg font-semibold text-stone-800">Yeni Sayfa Oluştur</h2>
            <p className="text-xs text-stone-500">Sayfa adı ve URL'sini girin, sonra düzenlemeye başlayın</p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-stone-200 p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1">Sayfa Adı</label>
            <input
              type="text"
              value={newPageName}
              onChange={e => {
                setNewPageName(e.target.value);
                if (!newPageSlug) setNewPageSlug(slugify(e.target.value));
              }}
              placeholder="Örn: Hakkımızda"
              className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1">Sayfa URL'si</label>
            <div className="flex items-center">
              <span className="px-3 py-2 bg-stone-100 border border-r-0 border-stone-200 rounded-l-lg text-xs text-stone-500 whitespace-nowrap">/</span>
              <input
                type="text"
                value={newPageSlug}
                onChange={e => { setNewPageSlug(slugify(e.target.value)); setSlugError(''); }}
                placeholder="hakkimizda"
                className="flex-1 border border-stone-200 rounded-r-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              />
            </div>
            {slugError && <p className="text-xs text-red-500 mt-1">{slugError}</p>}
            <p className="text-xs text-stone-400 mt-1">Sitenizde <strong>/{newPageSlug || 'url'}</strong> adresinde görünecek</p>
          </div>
          <button
            onClick={handleCreatePage}
            disabled={!newPageName.trim() || !newPageSlug.trim()}
            className="w-full bg-amber-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            Sayfayı Oluştur ve Düzenle
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-stone-800">Sayfalar</h2>
          <p className="text-xs text-stone-500">{pages.length} sayfa oluşturuldu</p>
        </div>
        <button
          onClick={() => setView('new')}
          className="flex items-center gap-2 bg-amber-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line" />
          Yeni Sayfa
        </button>
      </div>

      {pages.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-stone-300 p-12 text-center">
          <div className="w-14 h-14 flex items-center justify-center bg-stone-100 rounded-xl mx-auto mb-4">
            <i className="ri-pages-line text-2xl text-stone-400" />
          </div>
          <p className="text-sm font-medium text-stone-600">Henüz sayfa yok</p>
          <p className="text-xs text-stone-400 mt-1 mb-4">Section ve sütun düzeni ile özel sayfalar oluşturun</p>
          <button
            onClick={() => setView('new')}
            className="inline-flex items-center gap-2 bg-amber-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line" />
            İlk Sayfayı Oluştur
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {pages.map(page => (
            <div key={page.id} className="bg-white rounded-xl border border-stone-200 p-4 flex items-center gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-lg flex-shrink-0">
                <i className="ri-layout-line text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-stone-800 truncate">{page.name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs text-stone-400">/{page.seo.slug}</span>
                  <span className="text-stone-300">·</span>
                  <span className="text-xs text-stone-400">{page.sections.length} section</span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <a
                  href={`/${page.seo.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 transition-colors cursor-pointer text-stone-500 hover:text-stone-700"
                  title="Sayfayı Görüntüle"
                >
                  <i className="ri-external-link-line text-sm" />
                </a>
                <button
                  onClick={() => { setEditingPage(page); setView('edit'); }}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-amber-50 transition-colors cursor-pointer text-stone-500 hover:text-amber-600"
                  title="Düzenle"
                >
                  <i className="ri-edit-line text-sm" />
                </button>
                <button
                  onClick={() => handleDeletePage(page.id)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors cursor-pointer text-stone-400 hover:text-red-500"
                  title="Sil"
                >
                  <i className="ri-delete-bin-line text-sm" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
